import { useTranslation } from '../../../contexts/TranslationContext';
import { ChangeEvent, useRef, useState } from 'react';
import { device_controller } from '../../../controllers/device_controller';
import { DeviceInfoSection } from './DeviceInfoSection';
import { DisclaimerText, ErrorText } from '../MHM/MHMTypography';

export function DeviceImageUpload({
  deviceID,
  canEdit,
}: {
  deviceID: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  function validateImages(e: ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) {
      // e.target.files should never be null for file input
      // this is just to satisfy TypeScript type checking
      return;
    }

    if (e.target.files.length > 5) {
      setError('You can only upload up to 5 photos at a time');
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    const newImages = Array.from(e.target.files);
    const hasInvalidImageType = newImages.some(
      ({ type }) => !type.startsWith('image/'),
    );
    if (hasInvalidImageType) {
      setError('Invalid photos selected');
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    setError('');
    uploadImages(newImages);
  }

  async function uploadImages(files: File[]) {
    try {
      await device_controller.uploadImage(deviceID, files);
    } catch (e) {
      console.error(e);
      if (!(e instanceof Error) || !e.cause) {
        setError('Failed to upload photos. Please try again later');
        return;
      }

      setError(String(e.cause));
      return;
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
    device_controller.select_device(deviceID);
  }

  function handleButtonClick() {
    inputRef.current?.click();
  }

  return (
    <DeviceInfoSection.Entry direction="column" label={t('Upload New Photos:')}>
      <DisclaimerText>{t('Select up to 5 photos to upload')}</DisclaimerText>

      <input
        ref={inputRef}
        accept="image/*"
        type="file"
        multiple
        hidden
        onChange={validateImages}
      />
      {error ? <ErrorText>{t(error)}</ErrorText> : null}

      <DeviceInfoSection.Button
        size="large"
        onClick={handleButtonClick}
        variant="contained"
        disabled={!canEdit}
      >
        {t('Upload')}
      </DeviceInfoSection.Button>
    </DeviceInfoSection.Entry>
  );
}
